import global from "Global";
import { getLanguageCode } from "LanguageService";
import { numericLocale, timeInfo } from "LocaleSettings";
import userSession from "UserSession";
import { isVue2Build } from "VueMigration";
import { approvalBanners } from "VuePlugins/Captions/ApprovalBannersCaptions.ts";
import { autoRefresh } from "VuePlugins/Captions/AutoRefreshCaptions.ts";
import { changeBranchDepartment } from "VuePlugins/Captions/ChangeBranchDepartmentCaptions.ts";
import { changePassword } from "VuePlugins/Captions/ChangePasswordCaptions.ts";
import { choose } from "VuePlugins/Captions/ChooseCaptions.ts";
import { colorScheme } from "VuePlugins/Captions/ColorSchemeCaptions.ts";
import { conversation } from "VuePlugins/Captions/ConversationCaptions.ts";
import { customFields } from "VuePlugins/Captions/CustomFieldsCaptions.ts";
import { dateField, dateOffset, dateTimeField, timeField } from "VuePlugins/Captions/DateTimeFieldCaptions.ts";
import { dialog } from "VuePlugins/Captions/DialogCaptions.ts";
import { drawer } from "VuePlugins/Captions/DrawerCaptions.ts";
import { edoc } from "VuePlugins/Captions/EDocCaptions.ts";
import { errorReporting } from "VuePlugins/Captions/ErrorReportingCaptions.ts";
import { fileAttachment } from "VuePlugins/Captions/FileAttachmentCaptions.ts";
import { filters } from "VuePlugins/Captions/FiltersCaptions.ts";
import { headers } from "VuePlugins/Captions/HeadersCaptions.ts";
import { imagePicker } from "VuePlugins/Captions/ImagePickerCaptions.ts";
import { jobAddress } from "VuePlugins/Captions/JobAddressCaptions.ts";
import { layouts } from "VuePlugins/Captions/LayoutsCaptions.ts";
import { lookup } from "VuePlugins/Captions/LookupCaptions.ts";
import { map } from "VuePlugins/Captions/MapCaptions.ts";
import { menu } from "VuePlugins/Captions/MenuCaptions.ts";
import { notes } from "VuePlugins/Captions/NotesCaptions.ts";
import { passwordField } from "VuePlugins/Captions/PasswordFieldCaptions.ts";
import { schedule, weekDay } from "VuePlugins/Captions/ScheduleCaptions.ts";
import { searchControl } from "VuePlugins/Captions/SearchControlCaptions.ts";
import { searchDataTable } from "VuePlugins/Captions/SearchDataTableCaptions.ts";
import { searchList } from "VuePlugins/Captions/SearchListCaptions.ts";
import { themeConfiguration } from "VuePlugins/Captions/ThemeConfigurationCaptions.ts";
import { trackingTimeline } from "VuePlugins/Captions/TrackingTimelineCaptions.ts";
import { userImpersonation } from "VuePlugins/Captions/UserImpersonationCaptions.ts";
import { workflow } from "VuePlugins/Captions/WorkflowCaptions.ts";
import { addressField } from "VuePlugins/Captions/addressFieldCaptions.ts";
import DateFormatter from "VuePlugins/Formatters/DateFormatter";
import DateTimeFormatter from "VuePlugins/Formatters/DateTimeFormatter";
import TimeFormatter from "VuePlugins/Formatters/TimeFormatter";
import { refreshThemeOptionsAsync } from "VuePlugins/Theme/MaterialDesignThemeOptions.ts";
import Vue from "vue";
import WtgMaterialUi, { cargoWiseOneTheme, FrameworkType, supplyTheme, type ThemeOptions } from "wtg-material-ui";
import type { CaptionRecord, GetCaptionStringFunction } from "./Captions/types";

if (isVue2Build()) {
  Vue.use(WtgMaterialUi);
}

if (!global.materialDesign) {
  supplyTheme.controls.input = { attach: true };
}

const dateFormatter = new DateFormatter(timeInfo);
const dateTimeFormatter = new DateTimeFormatter(timeInfo);
const languageCode = getLanguageCode();
const timeFormatter = languageCode ? new TimeFormatter(timeInfo, languageCode) : undefined;

/* For adding a new caption property to the list below,
 * define it in a new file and import the file instead of defining the captions inline.
 * This helps adding some context (file name) for the translator to relate to the caption being translated.
 * */
const captions: CaptionRecord = {
  addressField,
  approvalBanners,
  autoRefresh,
  changePassword,
  changeBranchDepartment,
  choose,
  colorScheme,
  conversation,
  customFields,
  dateField,
  dateOffset,
  dateTimeField,
  dialog,
  drawer,
  edoc,
  errorReporting,
  fileAttachment,
  filters,
  headers,
  jobAddress,
  layouts,
  lookup,
  map,
  menu,
  notes,
  imagePicker,
  passwordField,
  schedule,
  searchControl,
  searchDataTable,
  searchList,
  themeConfiguration,
  timeField,
  trackingTimeline,
  userImpersonation,
  weekDay,
  workflow,
};

function captionProvider(language: string, key: string, params: unknown[] = []): string | undefined {
  let node: CaptionRecord | GetCaptionStringFunction | undefined = captions;

  for (const segment of key.split(".")) {
    if (node && typeof node === "object" && segment in node) {
      node = node[segment];
    } else {
      return undefined;
    }
  }

  return typeof node === "function" ? node(params) : undefined;
}

const frameworkVersion = global.portalInfo.frameworkVersion;
const theme = getThemeForFrameworkVersion();

class GlowWtgMaterialUi extends WtgMaterialUi {
  async handleApplicationStartAsync(): Promise<void> {
    await refreshFromEnvironmentAsync(userSession.isLoggedOn());
    userSession.onIsLoggedOnChanged(isLoggedOnHandler);
  }

  async refreshCurrentUserThemeAsync(): Promise<void> {
    const themeOptions: ThemeOptions | undefined = await refreshThemeOptionsAsync();
    if (themeOptions && themeOptions.themeVersion === theme.themeVersion) {
      this.installTheme(themeOptions, this.baseTheme);
    }
  }
}

/*! StartNoStringValidationRegion wtg library initialization */
const wtgMaterialUi = new GlowWtgMaterialUi({
  dark: global.portalInfo.defaultThemeAppearance === "Dark",
  framework: global.isPortable() && frameworkVersion! >= 7 ? FrameworkType.Mobile : undefined,
  language: {
    current: global.languageCode,
    captionProvider,
    dateFormatter,
    dateTimeFormatter,
    timeFormatter,
  },
  storage: {
    namespace: global.moduleName,
  },
  theme,
  frameworkVersion,
  supplyBetaComponents: global.featureFlags.supplyBetaComponents,
});

wtgMaterialUi.language.numericLocale = numericLocale;
/*! EndNoStringValidationRegion */

function getThemeForFrameworkVersion(): ThemeOptions {
  const frameworkVersion = global.portalInfo.frameworkVersion;
  if (frameworkVersion! >= 5) {
    const portalTheme = { ...supplyTheme };
    const portalThemeConfiguration = global.portalInfo.themeConfiguration;

    if (portalThemeConfiguration?.logoLight) {
      portalTheme.logoLightImage = portalThemeConfiguration.logoLight;
      portalTheme.logoLightImageFileType = portalThemeConfiguration.logoLightType;
    }

    if (portalThemeConfiguration?.logoDark) {
      portalTheme.logoDarkImage = portalThemeConfiguration.logoDark;
      portalTheme.logoDarkImageFileType = portalThemeConfiguration.logoDarkType;
    }

    if (portalThemeConfiguration?.brandColor) {
      portalTheme.colors = {
        ...supplyTheme.colors,
        light: {
          ...supplyTheme.colors.light,
          app: {
            ...supplyTheme.colors.light.app,
            brandColor: portalThemeConfiguration.brandColor,
            brandColorDark: !!portalThemeConfiguration.brandColorDark,
            brandLoginColor: portalThemeConfiguration.brandColor,
            brandLoginColorDark: !!portalThemeConfiguration.brandColorDark,
          },
        },
      };
    }

    return portalTheme;
  } else if (frameworkVersion! >= 4) {
    return cargoWiseOneTheme;
  } else {
    return supplyTheme;
  }
}

function isLoggedOnHandler(newValue: boolean): void {
  if (newValue && !userSession.isInvalidated()) {
    refreshFromEnvironmentAsync(true);
  }
}

async function refreshFromEnvironmentAsync(isLoggedOn: boolean): Promise<void> {
  wtgMaterialUi.language.current = isLoggedOn
    ? (userSession.sessionData().languageCode ?? global.languageCode)
    : global.languageCode;

  await wtgMaterialUi.refreshCurrentUserThemeAsync();
}

export default wtgMaterialUi;
